import { useMemo, useRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Line } from '@react-three/drei';
import * as THREE from 'three';
import { useScrollProgress, useIsLowPerformance } from '../../hooks';
import { useVideoMode } from '../../context';

// Color palette - Pro-Academy inspired
const COLORS = {
  gold: '#fbbf24',
  goldBright: '#fcd34d',
  pitch500: '#22c55e',
  pitch400: '#4ade80',
  pitch600: '#16a34a',
  pitch700: '#15803d',
  pitch800: '#166534',
  cyan: '#06b6d4',
  emerald: '#10b981',
  white: '#ffffff',
  net: '#aaaaaa',
};

// 3D Goal posts using Line - simple U-shape frame
function GoalPost({ position }: { position: [number, number, number] }) {
  const goalWidth = 3.6; // Width between posts (along Y)
  const goalHeight = 1.2; // Height of posts (along Z - perpendicular to field)
  
  // Goal frame as a continuous U-shape line
  // Starting from bottom-left, up the left post, across the crossbar, down the right post
  const framePoints: [number, number, number][] = [
    [0, -goalWidth / 2, 0],           // Bottom left (on field surface)
    [0, -goalWidth / 2, goalHeight],  // Top left
    [0, goalWidth / 2, goalHeight],   // Top right
    [0, goalWidth / 2, 0],            // Bottom right
  ];
  
  return (
    <group position={position}>
      <Line 
        points={framePoints} 
        color={COLORS.white} 
        lineWidth={4}
      />
    </group>
  );
}

// Soccer field with proper 3D perspective
function SoccerField({ scrollProgress, isVideoMode, isTransitioning }: { 
  scrollProgress: number; 
  isVideoMode: boolean;
  isTransitioning: boolean;
}) {
  const groupRef = useRef<THREE.Group>(null);
  
  // Field dimensions (in units, scaled for visual)
  const fieldLength = 20;
  const fieldWidth = 12;
  const halfLength = fieldLength / 2;
  const halfWidth = fieldWidth / 2;
  
  // Create all line data - field is on XY plane, Z is up
  const lineData = useMemo(() => {
    const lines: { points: [number, number, number][]; color: string; lineWidth: number; opacity: number }[] = [];
    
    // Main outer boundary
    lines.push({
      points: [[-halfLength, -halfWidth, 0], [halfLength, -halfWidth, 0], [halfLength, halfWidth, 0], [-halfLength, halfWidth, 0], [-halfLength, -halfWidth, 0]],
      color: COLORS.pitch500,
      lineWidth: 2.5,
      opacity: 0.9,
    });
    
    // Center line
    lines.push({
      points: [[0, -halfWidth, 0], [0, halfWidth, 0]],
      color: COLORS.gold,
      lineWidth: 2,
      opacity: 0.85,
    });
    
    // Penalty boxes
    const penaltyBoxLength = 3;
    const penaltyBoxWidth = 7;
    
    // Left penalty box
    lines.push({
      points: [[-halfLength, -penaltyBoxWidth / 2, 0], [-halfLength + penaltyBoxLength, -penaltyBoxWidth / 2, 0], [-halfLength + penaltyBoxLength, penaltyBoxWidth / 2, 0], [-halfLength, penaltyBoxWidth / 2, 0]],
      color: COLORS.pitch400,
      lineWidth: 1.5,
      opacity: 0.7,
    });
    
    // Right penalty box
    lines.push({
      points: [[halfLength, -penaltyBoxWidth / 2, 0], [halfLength - penaltyBoxLength, -penaltyBoxWidth / 2, 0], [halfLength - penaltyBoxLength, penaltyBoxWidth / 2, 0], [halfLength, penaltyBoxWidth / 2, 0]],
      color: COLORS.pitch400,
      lineWidth: 1.5,
      opacity: 0.7,
    });
    
    // Goal areas (6-yard box)
    const goalAreaLength = 1.5;
    const goalAreaWidth = 3.6;
    
    // Left goal area
    lines.push({
      points: [[-halfLength, -goalAreaWidth / 2, 0], [-halfLength + goalAreaLength, -goalAreaWidth / 2, 0], [-halfLength + goalAreaLength, goalAreaWidth / 2, 0], [-halfLength, goalAreaWidth / 2, 0]],
      color: COLORS.cyan,
      lineWidth: 1,
      opacity: 0.6,
    });
    
    // Right goal area
    lines.push({
      points: [[halfLength, -goalAreaWidth / 2, 0], [halfLength - goalAreaLength, -goalAreaWidth / 2, 0], [halfLength - goalAreaLength, goalAreaWidth / 2, 0], [halfLength, goalAreaWidth / 2, 0]],
      color: COLORS.cyan,
      lineWidth: 1,
      opacity: 0.6,
    });
    
    // Subtle grid lines
    for (let i = -4; i <= 4; i += 2) {
      if (i !== 0) {
        lines.push({
          points: [[-halfLength, i, 0], [halfLength, i, 0]],
          color: COLORS.pitch700,
          lineWidth: 0.5,
          opacity: 0.15,
        });
      }
    }
    
    for (let i = -8; i <= 8; i += 2) {
      if (i !== 0) {
        lines.push({
          points: [[i, -halfWidth, 0], [i, halfWidth, 0]],
          color: COLORS.pitch700,
          lineWidth: 0.5,
          opacity: 0.15,
        });
      }
    }
    
    return lines;
  }, [halfLength, halfWidth]);

  // Center circle
  const circlePoints = useMemo(() => {
    const points: [number, number, number][] = [];
    const segments = 64;
    const radius = 2.2;
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      points.push([Math.cos(angle) * radius, Math.sin(angle) * radius, 0]);
    }
    return points;
  }, []);

  // Penalty arcs
  const leftArcPoints = useMemo(() => {
    const points: [number, number, number][] = [];
    const segments = 24;
    for (let i = 0; i <= segments; i++) {
      const angle = -Math.PI / 3 + (i / segments) * (2 * Math.PI / 3);
      points.push([-halfLength + 3 + Math.cos(angle) * 1.5, Math.sin(angle) * 1.5, 0]);
    }
    return points;
  }, [halfLength]);

  const rightArcPoints = useMemo(() => {
    const points: [number, number, number][] = [];
    const segments = 24;
    for (let i = 0; i <= segments; i++) {
      const angle = Math.PI * 2 / 3 + (i / segments) * (2 * Math.PI / 3);
      points.push([halfLength - 3 + Math.cos(angle) * 1.5, Math.sin(angle) * 1.5, 0]);
    }
    return points;
  }, [halfLength]);

  // Animation targets
  const targetRotationX = useRef(-0.6); // Initial tilt to show perspective with goals
  
  useFrame(({ clock }, delta) => {
    if (!groupRef.current) return;
    
    const time = clock.elapsedTime;
    const lerpSpeed = 2 * delta;
    
    // Define targets based on mode
    if (isVideoMode || isTransitioning) {
      targetRotationX.current = -1.2; // More dramatic tilt for video
    } else {
      // Normal mode - subtle tilt to show goals
      targetRotationX.current = -0.55 - scrollProgress * 0.15;
    }
    
    // Smooth lerp to targets
    groupRef.current.rotation.x += (targetRotationX.current - groupRef.current.rotation.x) * lerpSpeed;
    
    // Ambient breathing
    const ambientStrength = isVideoMode ? 0.003 : 0.008;
    groupRef.current.rotation.z = Math.sin(time * 0.08) * ambientStrength;
    
    // Position based on scroll (normal) or video mode
    const targetY = isVideoMode ? 8 : 2 - scrollProgress * 8;
    const targetZ = isVideoMode ? -12 : -4 - scrollProgress * 6;
    
    groupRef.current.position.y += (targetY - groupRef.current.position.y) * lerpSpeed;
    groupRef.current.position.z += (targetZ - groupRef.current.position.z) * lerpSpeed;
    
    // Scale
    const targetScale = isVideoMode ? 1.3 : 0.85 + scrollProgress * 0.2;
    const currentScale = groupRef.current.scale.x;
    const newScale = currentScale + (targetScale - currentScale) * lerpSpeed;
    groupRef.current.scale.set(newScale, newScale, newScale);
  });

  return (
    <group ref={groupRef} position={[0, 2, -4]} rotation={[-0.6, 0, 0]}>
      {/* Goal posts */}
      <GoalPost position={[-halfLength, 0, 0]} />
      <GoalPost position={[halfLength, 0, 0]} />
      
      {/* Field lines */}
      {lineData.map((line, i) => (
        <Line
          key={`field-${i}`}
          points={line.points}
          color={line.color}
          lineWidth={line.lineWidth}
          transparent
          opacity={line.opacity}
        />
      ))}
      
      {/* Center circle */}
      <Line
        points={circlePoints}
        color={COLORS.gold}
        lineWidth={2}
        transparent
        opacity={0.8}
      />
      
      {/* Center spot */}
      <mesh position={[0, 0, 0.01]}>
        <circleGeometry args={[0.15, 16]} />
        <meshBasicMaterial color={COLORS.goldBright} transparent opacity={0.9} />
      </mesh>
      
      {/* Glow ring around center */}
      <mesh position={[0, 0, 0.01]}>
        <ringGeometry args={[2.3, 2.5, 64]} />
        <meshBasicMaterial color={COLORS.gold} transparent opacity={0.1} side={THREE.DoubleSide} />
      </mesh>
      
      {/* Penalty arcs */}
      <Line points={leftArcPoints} color={COLORS.emerald} lineWidth={1.5} transparent opacity={0.6} />
      <Line points={rightArcPoints} color={COLORS.emerald} lineWidth={1.5} transparent opacity={0.6} />
      
      {/* Penalty spots */}
      <mesh position={[-halfLength + 3, 0, 0.01]}>
        <circleGeometry args={[0.08, 12]} />
        <meshBasicMaterial color={COLORS.white} transparent opacity={0.7} />
      </mesh>
      <mesh position={[halfLength - 3, 0, 0.01]}>
        <circleGeometry args={[0.08, 12]} />
        <meshBasicMaterial color={COLORS.white} transparent opacity={0.7} />
      </mesh>
      
      {/* Corner arcs */}
      {[
        { pos: [-halfLength, -halfWidth, 0.01] as const, start: 0, end: Math.PI / 2 },
        { pos: [-halfLength, halfWidth, 0.01] as const, start: -Math.PI / 2, end: 0 },
        { pos: [halfLength, -halfWidth, 0.01] as const, start: Math.PI / 2, end: Math.PI },
        { pos: [halfLength, halfWidth, 0.01] as const, start: Math.PI, end: Math.PI * 3 / 2 },
      ].map((corner, i) => (
        <mesh key={`corner-${i}`} position={corner.pos}>
          <ringGeometry args={[0.4, 0.45, 16, 1, corner.start, corner.end - corner.start]} />
          <meshBasicMaterial color={COLORS.cyan} transparent opacity={0.5} side={THREE.DoubleSide} />
        </mesh>
      ))}
    </group>
  );
}

// Simplified tactical animation for mobile
function TacticalPlaysMobile({ scrollProgress }: { scrollProgress: number }) {
  const groupRef = useRef<THREE.Group>(null);
  const ballRef = useRef<THREE.Mesh>(null);
  
  useFrame(({ clock }) => {
    if (!groupRef.current || !ballRef.current) return;
    
    const time = clock.elapsedTime;
    
    // Simple ball animation - figure 8 pattern
    const x = Math.sin(time * 0.5) * 6;
    const y = Math.sin(time * 1.0) * 3;
    
    ballRef.current.position.set(x, y, 0.15);
    ballRef.current.rotation.set(time * 3, time * 3, 0);
    
    // Match field transform
    groupRef.current.position.y = 2 - scrollProgress * 8;
    groupRef.current.position.z = -4 - scrollProgress * 6;
    groupRef.current.rotation.x = -0.55 - scrollProgress * 0.15;
    
    const scale = 0.85 + scrollProgress * 0.2;
    groupRef.current.scale.set(scale, scale, scale);
  });
  
  return (
    <group ref={groupRef} position={[0, 2, -4]} rotation={[-0.6, 0, 0]}>
      {/* Ball */}
      <mesh ref={ballRef}>
        <icosahedronGeometry args={[0.2, 1]} />
        <meshBasicMaterial color={COLORS.white} />
      </mesh>
      
      {/* Glow */}
      <mesh position={[0, 0, 0.15]}>
        <circleGeometry args={[0.35, 16]} />
        <meshBasicMaterial color={COLORS.gold} transparent opacity={0.3} blending={THREE.AdditiveBlending} />
      </mesh>
    </group>
  );
}

// Full tactical plays (desktop only)
function TacticalPlaysDesktop({ scrollProgress, isVideoMode, isTransitioning }: { 
  scrollProgress: number;
  isVideoMode: boolean;
  isTransitioning: boolean;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const opacityRef = useRef(1);
  
  const playerARefs = useRef<(THREE.Group | null)[]>([]);
  const playerBRefs = useRef<(THREE.Group | null)[]>([]);
  const ballRef = useRef<THREE.Mesh>(null);
  const trailRef = useRef<THREE.Line | null>(null);
  const glowRef = useRef<THREE.Mesh>(null);

  const initialPositionsA = useMemo(() => [
    [-9, 0], [-6, -3.5], [-7, -1], [-7, 1], [-6, 3.5],
    [-4, 0], [-2, -2], [-2, 2], [2, -3], [3, 0], [2, 3],
  ], []);

  const initialPositionsB = useMemo(() => [
    [9.5, 0], [7, -3.5], [8, -1], [8, 1], [7, 3.5],
    [5, -3], [5, -1], [5, 1], [5, 3], [1, -1], [1, 1],
  ], []);

  const ID_OFFSET_B = 11;
  const totalCycle = 40;

  const script = useMemo(() => ({
    movements: [
      { id: 2, start: 0, duration: 3, target: [-6, -2] },
      { id: 3, start: 0, duration: 3, target: [-6, 2] },
      { id: 4, start: 2, duration: 5, target: [-1, 4] },
      { id: 6, start: 5, duration: 4, target: [1, -2] },
      { id: 9, start: 8, duration: 3, target: [6, 0] },
      { id: 16, start: 2, duration: 4, target: [3, -3] },
      { id: 13, start: 6, duration: 4, target: [6, -1] },
      { id: 12, start: 14, duration: 1, target: [6, -3] },
      { id: 16, start: 15, duration: 5, target: [-2, -4] },
      { id: 20, start: 16, duration: 6, target: [-6, -1] },
      { id: 21, start: 16, duration: 6, target: [-6, 2] },
      { id: 17, start: 18, duration: 5, target: [-2, -1] },
      { id: 1, start: 16, duration: 6, target: [-5, -3] },
      { id: 5, start: 17, duration: 5, target: [-5, 0] },
      { id: 2, start: 27, duration: 2, target: [-8, -1] },
      ...initialPositionsA.map((pos, i) => ({ id: i, start: 32, duration: 8, target: pos })),
      ...initialPositionsB.map((pos, i) => ({ id: i + ID_OFFSET_B, start: 32, duration: 8, target: pos }))
    ],
    passes: [
      { from: 0, to: 2, start: 1, duration: 1.5, height: 0 },
      { from: 2, to: 5, start: 3, duration: 1.2, height: 0 },
      { from: 5, to: 4, start: 5, duration: 2.0, height: 0.5 },
      { from: 4, to: 7, start: 8, duration: 1.2, height: 0 },
      { from: 7, to: 9, start: 10, duration: 1.5, height: 0 },
      { from: 9, to: 12, start: 12.5, duration: 1.5, height: 0.2 },
      { from: 12, to: 16, start: 15, duration: 1.2, height: 0 },
      { from: 16, to: 20, start: 17, duration: 2.5, height: 0.5 },
      { from: 20, to: 21, start: 20.5, duration: 1.5, height: 0 },
      { from: 21, to: 17, start: 23, duration: 1.5, height: 0 },
      { from: 17, to: 2, start: 25.5, duration: 1.5, height: 0.2 },
      { from: 2, to: 0, start: 28, duration: 1.5, height: 0 },
    ]
  }), [initialPositionsA, initialPositionsB]);

  const trailPoints = useRef<THREE.Vector3[]>(new Array(50).fill(null).map(() => new THREE.Vector3(0, 0, 0)));

  useFrame(({ clock }, delta) => {
    if (!groupRef.current) return;
    
    const time = clock.elapsedTime;
    const cycleTime = time % totalCycle;

    const getPlayerPos = (id: number, initialPosArray: number[][], offset: number) => {
      const localId = id - offset;
      const initialPos = initialPosArray[localId];
      
      const moves = script.movements.filter(m => m.id === id && m.start <= cycleTime).sort((a, b) => a.start - b.start);
      if (moves.length === 0) return initialPos;
      
      const lastMove = moves[moves.length - 1];
      const moveEnd = lastMove.start + lastMove.duration;
      
      if (cycleTime < moveEnd) {
        const progress = (cycleTime - lastMove.start) / lastMove.duration;
        const ease = progress < 0.5 ? 2 * progress * progress : -1 + (4 - 2 * progress) * progress;
        let startPos = initialPos;
        if (moves.length > 1) startPos = moves[moves.length - 2].target;
        return [startPos[0] + (lastMove.target[0] - startPos[0]) * ease, startPos[1] + (lastMove.target[1] - startPos[1]) * ease];
      }
      return lastMove.target;
    };

    const currentPosA: number[][] = [];
    playerARefs.current.forEach((ref, i) => {
      const pos = getPlayerPos(i, initialPositionsA, 0);
      currentPosA[i] = pos;
      if (ref) {
        ref.position.set(pos[0], pos[1], 0.1);
        ref.scale.setScalar(1 + Math.sin(time * 3 + i) * 0.05);
      }
    });

    const currentPosB: number[][] = [];
    playerBRefs.current.forEach((ref, i) => {
      const id = i + ID_OFFSET_B;
      const pos = getPlayerPos(id, initialPositionsB, ID_OFFSET_B);
      currentPosB[i] = pos;
      if (ref) {
        ref.position.set(pos[0], pos[1], 0.1);
        ref.scale.setScalar(1 + Math.sin(time * 3 + id) * 0.05);
      }
    });

    let ballActive = false;
    let ballPos = new THREE.Vector3(0, 0, -10);
    const activePass = script.passes.find(p => cycleTime >= p.start && cycleTime <= p.start + p.duration);
    
    if (activePass) {
      ballActive = true;
      const progress = (cycleTime - activePass.start) / activePass.duration;
      const getCoords = (id: number) => id < ID_OFFSET_B ? currentPosA[id] : currentPosB[id - ID_OFFSET_B];
      const startPos = getCoords(activePass.from);
      const endPos = getCoords(activePass.to);
      if (startPos && endPos) {
        const x = startPos[0] + (endPos[0] - startPos[0]) * progress;
        const y = startPos[1] + (endPos[1] - startPos[1]) * progress;
        const height = Math.sin(progress * Math.PI) * (activePass.height || 0.2);
        ballPos.set(x, y, 0.2 + height);
      }
    } else {
      const lastPass = [...script.passes].reverse().find(p => p.start + p.duration <= cycleTime);
      let holderId = 0;
      if (lastPass) holderId = lastPass.to;
      let holderPos = holderId < ID_OFFSET_B ? currentPosA[holderId] : currentPosB[holderId - ID_OFFSET_B];
      if (holderPos) {
        ballActive = true;
        const dribble = Math.sin(time * 15) * 0.15;
        ballPos.set(holderPos[0] + 0.2, holderPos[1] + dribble, 0.15);
      }
    }

    if (ballRef.current && glowRef.current) {
      ballRef.current.position.copy(ballPos);
      ballRef.current.visible = ballActive;
      ballRef.current.rotation.set(time * 5, time * 5, 0);
      glowRef.current.position.copy(ballPos);
      glowRef.current.visible = ballActive;
    }

    if (trailRef.current && ballActive) {
      const points = trailPoints.current;
      points.pop();
      points.unshift(ballPos.clone());
      trailRef.current.geometry.setFromPoints(points);
      if (points[0].distanceTo(points[1]) > 3) {
        for (let i = 0; i < points.length; i++) points[i].copy(points[0]);
        trailRef.current.geometry.setFromPoints(points);
      }
    }

    const lerpSpeed = 3 * delta;
    const targetOpacity = (isVideoMode || isTransitioning) ? 0 : 1;
    opacityRef.current += (targetOpacity - opacityRef.current) * lerpSpeed;
    
    playerARefs.current.forEach((ref) => {
      if (ref) ref.children.forEach((child) => {
        if ((child as THREE.Mesh).material) ((child as THREE.Mesh).material as THREE.MeshBasicMaterial).opacity = opacityRef.current * 0.9;
      });
    });
    playerBRefs.current.forEach((ref) => {
      if (ref) ref.children.forEach((child) => {
        if ((child as THREE.Mesh).material) ((child as THREE.Mesh).material as THREE.MeshBasicMaterial).opacity = opacityRef.current * 0.9;
      });
    });
    
    if (ballRef.current) (ballRef.current.material as THREE.MeshBasicMaterial).opacity = opacityRef.current;
    if (glowRef.current) (glowRef.current.material as THREE.MeshBasicMaterial).opacity = opacityRef.current * 0.5;
    if (trailRef.current) (trailRef.current.material as THREE.LineBasicMaterial).opacity = opacityRef.current * 0.6;

    // Match field transform
    const targetY = isVideoMode ? 8 : 2 - scrollProgress * 8;
    const targetZ = isVideoMode ? -12 : -4 - scrollProgress * 6;
    const targetRotX = isVideoMode ? -1.2 : -0.55 - scrollProgress * 0.15;
    
    groupRef.current.position.y += (targetY - groupRef.current.position.y) * lerpSpeed;
    groupRef.current.position.z += (targetZ - groupRef.current.position.z) * lerpSpeed;
    groupRef.current.rotation.x += (targetRotX - groupRef.current.rotation.x) * lerpSpeed;
    groupRef.current.rotation.z = Math.sin(time * 0.08) * 0.008;
    
    const targetScale = isVideoMode ? 1.3 : 0.85 + scrollProgress * 0.2;
    const currentScale = groupRef.current.scale.x;
    const newScale = currentScale + (targetScale - currentScale) * lerpSpeed;
    groupRef.current.scale.set(newScale, newScale, newScale);
  });
  
  return (
    <group ref={groupRef} position={[0, 2, -4]} rotation={[-0.6, 0, 0]}>
      {initialPositionsA.map((_, i) => (
        <group key={`pA-${i}`} ref={(el) => (playerARefs.current[i] = el)}>
          <mesh><circleGeometry args={[0.3, 16]} /><meshBasicMaterial color={i === 0 ? COLORS.cyan : COLORS.gold} transparent opacity={0.9} /></mesh>
          <mesh><ringGeometry args={[0.35, 0.45, 16]} /><meshBasicMaterial color={COLORS.gold} transparent opacity={0.5} /></mesh>
        </group>
      ))}
      {initialPositionsB.map((_, i) => (
        <group key={`pB-${i}`} ref={(el) => (playerBRefs.current[i] = el)}>
          <mesh><circleGeometry args={[0.3, 16]} /><meshBasicMaterial color={i === 0 ? COLORS.gold : COLORS.cyan} transparent opacity={0.9} /></mesh>
          <mesh><ringGeometry args={[0.35, 0.45, 16]} /><meshBasicMaterial color={COLORS.cyan} transparent opacity={0.5} /></mesh>
        </group>
      ))}
      <mesh ref={ballRef}><icosahedronGeometry args={[0.18, 1]} /><meshBasicMaterial color={COLORS.white} /></mesh>
      <mesh ref={glowRef}><circleGeometry args={[0.3, 16]} /><meshBasicMaterial color={COLORS.white} transparent opacity={0.5} blending={THREE.AdditiveBlending} /></mesh>
      <line ref={trailRef as any}><bufferGeometry /><lineBasicMaterial color={COLORS.goldBright} transparent opacity={0.6} /></line>
    </group>
  );
}

// Camera animation - orbits to right goal and back
function CameraAnimation({ isVideoMode, isTransitioning }: { isVideoMode: boolean; isTransitioning: boolean }) {
  const { camera } = useThree();
  const timeRef = useRef(0);
  
  // Normal: looking at field from front
  const normalPos = useMemo(() => new THREE.Vector3(0, 3, 16), []);
  const normalTarget = useMemo(() => new THREE.Vector3(0, 0, 0), []);
  
  // Video mode: behind goal looking at screen
  const videoPos = useMemo(() => new THREE.Vector3(0, -8, 6), []);
  const videoTarget = useMemo(() => new THREE.Vector3(0, 10, 0), []);
  
  const currentTarget = useRef(new THREE.Vector3(0, 0, 0));
  
  useFrame(({ clock }, delta) => {
    timeRef.current = clock.elapsedTime;
    const lerpSpeed = 1.5 * delta;
    
    // Subtle camera sway in normal mode
    let targetPos = normalPos.clone();
    let targetLook = normalTarget.clone();
    
    if (isVideoMode || isTransitioning) {
      targetPos = videoPos;
      targetLook = videoTarget;
    } else {
      // Gentle orbit animation toward right goal
      const orbitPhase = Math.sin(timeRef.current * 0.15) * 0.5 + 0.5; // 0 to 1
      const orbitAngle = orbitPhase * Math.PI * 0.25; // 0 to 45 degrees toward right
      
      targetPos.x = Math.sin(orbitAngle) * 18;
      targetPos.z = Math.cos(orbitAngle) * 16;
      targetPos.y = 3 + orbitPhase * 2;
      
      // Look slightly toward right goal
      targetLook.x = orbitPhase * 5;
    }
    
    camera.position.lerp(targetPos, lerpSpeed);
    currentTarget.current.lerp(targetLook, lerpSpeed);
    camera.lookAt(currentTarget.current);
  });
  
  return null;
}

// Floating particles
function EnergyParticles({ scrollProgress }: { scrollProgress: number }) {
  const particlesRef = useRef<THREE.Points>(null);
  
  const { geometry } = useMemo(() => {
    const count = 60;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    
    const colorOptions = [
      new THREE.Color(COLORS.pitch500),
      new THREE.Color(COLORS.gold),
      new THREE.Color(COLORS.cyan),
    ];
    
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = Math.random() * 15;
      positions[i * 3] = Math.cos(angle) * radius;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 10;
      positions[i * 3 + 2] = Math.sin(angle) * radius - 5;
      
      const color = colorOptions[Math.floor(Math.random() * colorOptions.length)];
      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
    }
    
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geom.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    return { geometry: geom };
  }, []);

  useFrame(({ clock }) => {
    if (!particlesRef.current) return;
    const time = clock.elapsedTime;
    const positions = particlesRef.current.geometry.attributes.position.array as Float32Array;
    
    for (let i = 0; i < positions.length / 3; i++) {
      positions[i * 3 + 1] += Math.sin(time + i) * 0.003;
    }
    
    particlesRef.current.geometry.attributes.position.needsUpdate = true;
    const mat = particlesRef.current.material as THREE.PointsMaterial;
    mat.opacity = 0.5 * (1 - scrollProgress * 0.5);
    particlesRef.current.rotation.y = time * 0.01;
  });

  return (
    <points ref={particlesRef} geometry={geometry}>
      <pointsMaterial size={0.1} transparent opacity={0.5} vertexColors sizeAttenuation blending={THREE.AdditiveBlending} />
    </points>
  );
}

// Main scene
function Scene({ scrollProgress, isMobile, isVideoMode, isTransitioning }: { 
  scrollProgress: number; 
  isMobile: boolean;
  isVideoMode: boolean;
  isTransitioning: boolean;
}) {
  return (
    <>
      <CameraAnimation isVideoMode={isVideoMode} isTransitioning={isTransitioning} />
      <SoccerField scrollProgress={scrollProgress} isVideoMode={isVideoMode} isTransitioning={isTransitioning} />
      {isMobile ? (
        <TacticalPlaysMobile scrollProgress={scrollProgress} />
      ) : (
        <TacticalPlaysDesktop scrollProgress={scrollProgress} isVideoMode={isVideoMode} isTransitioning={isTransitioning} />
      )}
      <EnergyParticles scrollProgress={scrollProgress} />
      <ambientLight intensity={0.15} />
    </>
  );
}

export function GlobalBackground() {
  const scrollProgress = useScrollProgress();
  const isLowPerf = useIsLowPerformance();
  const { isVideoMode, isTransitioning } = useVideoMode();
  
  // Use window width for mobile detection (simpler, works in SSR)
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  
  if (isLowPerf) return null;

  return (
    <div className="fixed inset-0 z-0 pointer-events-none">
      <Canvas
        camera={{ position: [0, 3, 16], fov: 50 }}
        dpr={isMobile ? 1 : [1, 1.5]}
        gl={{ antialias: !isMobile, alpha: true, powerPreference: 'high-performance' }}
      >
        <Scene scrollProgress={scrollProgress} isMobile={isMobile} isVideoMode={isVideoMode} isTransitioning={isTransitioning} />
      </Canvas>
    </div>
  );
}
